import { cn } from "@aurem/ui";
import type { HTMLAttributes, JSX, PropsWithChildren, ReactNode } from "react";
import { Link, useMatch, useResolvedPath } from "react-router-dom";

export type InnerSidebarProps = {
  children: ReactNode;
  header: JSX.Element;
  footer?: JSX.Element;
  /** @deprecated */
  setSeparator?: boolean;
};

export const InnerSidebar = ({
  children,
  header,
  footer,
}: InnerSidebarProps) => {
  return (
    <aside className="hidden sm:flex flex-col overflow-hidden bg-secondary-500 text-white w-[200px] shrink-0">
      {header}
      <ul className="flex-1 overflow-y-auto p-4 space-y-1">{children}</ul>
      <ul className="p-4 space-y-1">{footer}</ul>
    </aside>
  );
};

export type HeaderProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>>;

export const Header = ({ className, ...props }: HeaderProps) => {
  return (
    <div
      className={cn(
        "h-16 w-full flex items-center justify-between shrink-0 gap-2 px-4 py-1.5 border-b border-b-neutral-300",
        className,
      )}
      {...props}
    ></div>
  );
};

export type InnerSidebarItemProps = {
  label: string;
  children?: typeof InnerSidebarItem | (typeof InnerSidebarItem)[];
  /** Renders a <Link> when set. Mutually exclusive with `onClick`. */
  to?: string;
  /** Renders a <button> when set and `to` is omitted. */
  onClick?: () => void;
  /** Path pattern for detecting whether the link is active; resolved to is used by default */
  match?: string;
  e2eId?: string;
  className?: string;
};

const itemClassName = (isSelected: boolean, extra?: string) =>
  cn(
    "flex w-full text-start justify-between rounded px-4 py-3 text-sm font-medium transition-colors",
    "focus-visible:outline focus-visible:outline-1 focus-visible:outline-white focus-visible:outline-offset-2",
    isSelected
      ? "bg-white text-secondary-500"
      : "hover:bg-white/10 aria-pressed:bg-white/20 active:bg-white/20",
    extra,
  );

export const InnerSidebarItem = ({
  label,
  to,
  onClick,
  match,
  e2eId,
  className,
}: InnerSidebarItemProps) => {
  match ??= to ?? "";
  const resolved = useResolvedPath(match);
  const routeMatch = useMatch({ path: resolved.pathname, end: false });
  const isSelected = !!to && !!routeMatch;
  return (
    <li className="list-none">
      {to ? (
        <Link
          data-e2e-id={e2eId}
          to={to}
          className={itemClassName(isSelected, className)}
        >
          {label}
        </Link>
      ) : (
        <button
          type="button"
          data-e2e-id={e2eId}
          onClick={onClick}
          className={itemClassName(false, className)}
        >
          {label}
        </button>
      )}
    </li>
  );
};
